/** One Web Audio clock for both tones and preloaded speech. Never catch up missed cues. */
import voiceClips from './voiceClips';
export class AudioScheduler {
  constructor({ onError = () => {}, voiceEnabled = false } = {}) {
    this.ctx = null; this.events = []; this.nextIndex = 0;
    this.startCtxTime = 0; this.timer = null; this.running = false;
    this.voiceEnabled = voiceEnabled; this.onError = onError;
    this.nodes = new Set(); this.buffers = new Map(); this.frozenElapsed = 0;
    this.destroyed = false; this.generation = 0;
    this._loop = this._loop.bind(this);
    this.audioSession = typeof navigator !== 'undefined' ? navigator.audioSession : null;
    this._sessionChange = () => { if (this.running && this.audioSession?.state === 'interrupted') this.fail('Telefonen avbröt ljudet. Testpasset finns kvar.'); };
    this.audioSession?.addEventListener?.('statechange', this._sessionChange);
    // Visibility changes are not an audio interruption. All cues are queued
    // on the audio clock before playback; a throttled JS timer cannot lose beeps.
  }
  setVoiceEnabled(v) { this.voiceEnabled = !!v; }
  async unlock() {
    const generation = this.generation;
    try {
      try { if (navigator.audioSession) navigator.audioSession.type = 'playback'; } catch { /* optional API */ }
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) throw new Error('Ljud stöds inte.');
      if (!this.ctx) {
        this.ctx = new AC();
        this.ctx.onstatechange = () => {
          if (this.running && this.ctx.state !== 'running') this.fail('Telefonen eller webbläsaren avbröt ljudet. Testpasset finns kvar.');
        };
      }
      // Resume inside the start gesture, before fetching speech.
      await this.ctx.resume();
      if (this.destroyed || generation !== this.generation) return false;
      if (!this.loadPromise) this.loadPromise = Promise.all(Object.entries(voiceClips).map(async ([text, url]) => {
        if (this.buffers.has(text)) return;
        const response = await fetch(url);
        if (!response.ok) throw new Error('Testljuden kunde inte laddas.');
        if (this.destroyed || generation !== this.generation) return;
        const buffer = await this.ctx.decodeAudioData(await response.arrayBuffer());
        this.buffers.set(text, buffer);
      })).catch(e => { this.loadPromise = null; throw e; });
      await this.loadPromise;
      if (this.destroyed || generation !== this.generation) return false;
      if (this.ctx.state !== 'running') await this.ctx.resume();
      if (this.destroyed || generation !== this.generation) return false;
      if (this.ctx.state !== 'running') throw new Error('Ljudet kunde inte startas.');
      return true;
    } catch (e) { if (!this.destroyed && generation === this.generation) this.onError(e); return false; }
  }
  setEvents(events) { this.events = [...events].sort((a,b) => a.time-b.time); this.nextIndex = 0; }
  start() {
    this.stopNodes(); this.frozenElapsed = 0;
    this.startCtxTime = this.ctx.currentTime + 0.75;
    this.nextIndex = 0; this.running = true;
    this.beginLoop();
  }
  beginLoop() {
    try { this._loop(); } catch { this.fail('Ljudet kunde inte förberedas. Testpasset finns kvar.'); }
  }
  _loop() {
    if (!this.running || !this.ctx) return;
    if (this.ctx.state !== 'running') { this.fail('Ljudet avbröts.'); return; }
    const elapsed = this.ctx.currentTime - this.startCtxTime;
    while (this.nextIndex < this.events.length) {
      const ev = this.events[this.nextIndex];
      const audible = ev.category === 'beep' || (this.voiceEnabled && ev.category === 'voice');
      if (audible && elapsed - ev.time > 0.08) {
        this.fail('En ljudsignal blev försenad. Starta om testet för ett jämförbart resultat.'); return;
      }
      const when = Math.max(this.startCtxTime + ev.time, this.ctx.currentTime);
      if (ev.category === 'beep') this._beep(when, ev);
      else if (this.voiceEnabled && ev.category === 'voice' && ev.speak) {
        const buffer = this.buffers.get(ev.speak);
        if (!buffer) { this.fail('En ljudsignal saknas.'); return; }
        const node = this.ctx.createBufferSource(); node.buffer = buffer;
        node.connect(this.ctx.destination); this.track(node); node.start(when);
      }
      this.nextIndex++;
    }
  }
  track(node) { this.nodes.add(node); node.onended = () => { this.nodes.delete(node); node.disconnect(); }; }
  stopNodes() {
    for (const n of this.nodes) { try { n.stop(); n.disconnect(); } catch { /* already ended */ } }
    this.nodes.clear();
  }
  _beep(when, ev) {
    const ctx = this.ctx, duration = 0.13;
    // Gentle harmonics improve definition on small speakers; Web Audio normalizes the peak.
    if (!this.beepWave) this.beepWave = ctx.createPeriodicWave(
      new Float32Array([0, 0, 0, 0]), new Float32Array([0, 1, 0.22, 0.08])
    );
    for (let i=0; i<(ev.count || 1); i++) {
      const at = when + i * 0.17;
      const osc = ctx.createOscillator(), gain = ctx.createGain();
      osc.setPeriodicWave(this.beepWave);
      osc.frequency.setValueAtTime(ev.freq || 1000, at);
      gain.gain.setValueAtTime(0, at); gain.gain.linearRampToValueAtTime(0.90, at+0.012);
      gain.gain.setValueAtTime(0.90, at+duration-0.03);
      gain.gain.linearRampToValueAtTime(0, at+duration);
      osc.connect(gain); gain.connect(ctx.destination); this.track(osc);
      osc.onended = () => { this.nodes.delete(osc); osc.disconnect(); gain.disconnect(); };
      osc.start(at); osc.stop(at+duration+0.02);
    }
  }
  getElapsed() { return this.running ? Math.max(this.frozenElapsed, this.ctx.currentTime-this.startCtxTime) : this.frozenElapsed; }
  halt() {
    this.frozenElapsed = this.getElapsed(); this.running = false;
    if (this.timer) clearInterval(this.timer); this.timer = null;
  }
  fail(message) { this.halt(); this.stopNodes(); this.onError(new Error(message)); }
  async pause() {
    this.generation++; this.loadPromise = null;
    this.halt(); this.stopNodes(); // Cancel future cues before suspending.
    if (this.ctx?.state === 'running') await this.ctx.suspend();
  }
  async resume() {
    if (!(await this.unlock()) || this.destroyed) return false;
    this.seekTo(this.frozenElapsed, true);
    return this.running;
  }
  restore(time) { this.reset(); this.frozenElapsed = Math.max(0, Number(time) || 0); }
  seekTo(time, resuming = false) {
    this.halt(); this.stopNodes();
    this.startCtxTime = this.ctx.currentTime + 0.75 - time; this.frozenElapsed = time;
    this.nextIndex = this.events.findIndex(e => resuming ? e.time > time : e.time >= time);
    if (this.nextIndex < 0) this.nextIndex = this.events.length;
    this.running = true; this.beginLoop();
  }
  finish(time) { this.halt(); this.frozenElapsed = time; } // Let the final tone finish.
  reset() { this.generation++; this.loadPromise = null; this.halt(); this.stopNodes(); this.nextIndex = 0; this.frozenElapsed = 0; }
  destroy() {
    this.reset();
    this.destroyed = true; this.generation++;
    this.audioSession?.removeEventListener?.('statechange', this._sessionChange);
    if (this.ctx) { this.ctx.onstatechange = null; this.ctx.close().catch(() => {}); this.ctx = null; }
  }
}
export default AudioScheduler;
